// The buttons under anything that waits for a person: Approve, Revise, Deny — the same in a room, inside a
// thread and on Home, for a Hermes gate and for an agent's approval card.
// "Always" is Hermes only (go ahead, and stop asking for this kind of action), so cards do not show it.
import React, { useState } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import { Text } from './Text';
import { ReviseSheet } from './ReviseSheet';
import type { GateChoice } from '../matrix';
import { radius, makeStyles, useTheme } from '../theme';

export function GateBar({ onAnswer, always = true, about }: {
  onAnswer: (choice: GateChoice, note?: string) => void;
  always?: boolean;
  /** A few words on what is being decided, shown in the Revise sheet. */
  about?: string;
}) {
  const s = useStyles();
  const [asking, setAsking] = useState(false);
  return (
    <View style={s.row}>
      <Btn label="Approve" kind="approve" onPress={() => onAnswer('approve')} />
      <Btn label="Revise" kind="neutral" onPress={() => setAsking(true)} />
      <Btn label="Deny" kind="deny" onPress={() => onAnswer('deny')} />
      {always && <Btn label="Always" kind="quiet" onPress={() => onAnswer('always')} />}
      <ReviseSheet visible={asking} about={about} onCancel={() => setAsking(false)} onSend={(note) => { setAsking(false); onAnswer('revise', note); }} />
    </View>
  );
}

/** What was decided, for the line that replaces the buttons. */
export const DECIDED: Record<GateChoice, string> = {
  approve: '✓ Approved', always: '✓ Approved, and will not ask again', revise: '↩ Sent back for changes', deny: '✕ Denied',
};

function Btn({ label, kind, onPress }: { label: string; kind: 'approve' | 'deny' | 'neutral' | 'quiet'; onPress: () => void }) {
  const theme = useTheme(); const s = useStyles();
  return (
    <Pressable onPress={onPress} style={({ pressed }) => [s.btn, s[kind], pressed && { opacity: 0.6 }]} accessibilityRole="button" accessibilityLabel={label}>
      <Text style={[s.text, kind === 'deny' && { color: theme.deny }, kind === 'approve' && { color: theme.approve }, kind === 'quiet' && { color: theme.muted }]}>{label}</Text>
    </Pressable>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  row: { flexDirection: 'row', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  btn: { paddingHorizontal: 14, paddingVertical: 9, borderRadius: radius.md, borderWidth: 1 },
  approve: { backgroundColor: theme.approveBg, borderColor: theme.approveBorder },
  deny: { backgroundColor: 'transparent', borderColor: theme.denyBorder },
  neutral: { backgroundColor: theme.surface2, borderColor: theme.border },
  quiet: { backgroundColor: 'transparent', borderColor: theme.border },
  text: { color: theme.ink, fontWeight: '700', fontSize: 13.5 },
}));
